<script setup>
import { ref, watch } from 'vue'
import { useNotificationStore } from '@/stores/NotificationStore.js'

const props = defineProps({
    lead: {
        type: Object,
        required: true,
    },
    allowDelete: {
        type: Boolean,
        required: false,
        default: true,
    },
    alignment: {
        type: String,
        required: false,
        default: 'right',
    },
    cancelOnOutsideClick: {
        type: Boolean,
        required: false,
        default: false,
    },
    allowArchive: {
        type: Boolean,
        required: false,
        default: false,
    },
    // When false, emit 'deleted' instead of navigating to the lead list (e.g. in the quickview slideout).
    redirectAfterDelete: {
        type: Boolean,
        required: false,
        default: true,
    },
    showLabel: {
        type: Boolean,
        required: false,
        default: true,
    },
})

const emit = defineEmits(['reload:lead', 'archived', 'deleted'])
const notificationStore = useNotificationStore()
const deleteDialogOpen = ref(false)
const isContactSynced = ref(false)
const deleteContextState = ref('idle')

const unsubscribeLead = async () => {
    await axios.get(route('crm.leads.unsubscribe', props.lead.id))

    notificationStore.addNotification({
        type: 'success',
        title: _mft('crm:lead.unsubscribe.alreadyUnsubscribed'),
        message: props.lead.email || props.lead.name || props.lead.company || null,
        dismissAfter: 3000,
    })

    emit('reload:lead')
}

const sendResubscribeConfirmation = async () => {
    try {
        await axios.post(route('crm.leads.resubscribe.confirmation', props.lead.id))

        notificationStore.addNotification({
            type: 'success',
            title: _mft('crm:lead.resubscribe.confirmationSent'),
            message: props.lead.email || props.lead.name || props.lead.company || null,
            dismissAfter: 3000,
        })

        emit('reload:lead')
    } catch (error) {
        console.error('Error sending resubscribe confirmation:', error)
    }
}

const archiveLead = async () => {
    try {
        const res = await axios.post(route('api.crm.leads.archive'), {lead_ids: [props.lead.id]})

        notificationStore.addNotification({
            type: 'success',
            title: _mft('crm:archive.result.archived', {count: res.data.succeeded}),
            dismissAfter: 5000,
        })

        emit('archived')
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('crm:archive.error'),
            dismissAfter: 5000,
        })
    }
}

const deleteLead = async () => {
    try {
        await axios.post(route('crm.leads.delete', props.lead.id))

        if (! props.redirectAfterDelete) {
            deleteDialogOpen.value = false
            notificationStore.addNotification({
                type: 'success',
                title: _mft('crm:lead.delete.deleted'),
                message: props.lead.email || props.lead.name || props.lead.company || null,
                dismissAfter: 3000,
            })
            emit('deleted')

            return
        }

        window.location.href = route('crm.leads')
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('crm:someLeadsNotDeleted'),
            dismissAfter: 3000,
        })
    }
}

const loadDeleteContext = async () => {
    deleteContextState.value = 'loading'
    isContactSynced.value = false

    try {
        const response = await axios.get(route('api.crm.lead.delete-context', props.lead.id))
        isContactSynced.value = response.data?.is_contact_synced === true
        deleteContextState.value = 'loaded'
    } catch (error) {
        console.error('Error loading lead delete context:', error)
        deleteContextState.value = 'error'
    }
}

watch(deleteDialogOpen, (isOpen) => {
    if (isOpen) {
        void loadDeleteContext()
    } else {
        deleteContextState.value = 'idle'
        isContactSynced.value = false
    }
})
</script>

<template>
    <button-more-options
        :label="showLabel ? _mft('crm:lead.actions') : ''"
        :alignment="alignment"
        :cancel-on-outside-click="cancelOnOutsideClick"
    >
        <button-more-options-option-with-confirmation
            v-if="!lead.crm_communications_email_opted_out"
            style-type="danger"
            :confirm-text="_mft('crm:lead.unsubscribe.cannotBeUndone')"
            :confirm-button-label="_mft('crm:lead.unsubscribe.confirm')"
            :abort-button-label="_mft('shared:action.cancel')"
            @confirmed="unsubscribeLead"
            class="w-48"
        >
            {{ _mft('crm:lead.unsubscribe.label') }}
        </button-more-options-option-with-confirmation>

        <button-more-options-option
            v-if="lead.crm_communications_email_opted_out && lead.resubscribe_confirmation_sent_at && !lead.resubscribe_confirmation_declined_at"
            class="italic text-gray-400 cursor-not-allowed"
        >
            {{ _mft('crm:lead.unsubscribe.alreadyUnsubscribed') }} - {{ _mft('crm:lead.resubscribe.confirmationSent') }}
        </button-more-options-option>

        <button-more-options-option
            v-if="lead.crm_communications_email_opted_out && lead.resubscribe_confirmation_declined_at"
            class="italic text-gray-400 cursor-not-allowed"
        >
            {{ _mft('crm:lead.unsubscribe.alreadyUnsubscribed') }} - {{ _mft('crm:lead.resubscribe.declined') }}
        </button-more-options-option>

        <button-more-options-option
            v-if="lead.crm_communications_email_opted_out && !lead.resubscribe_confirmation_sent_at && !lead.resubscribe_confirmation_declined_at"
            @click="sendResubscribeConfirmation"
            class="text-green-600 hover:text-green-700"
        >
            {{ _mft('crm:lead.resubscribe.label') }}
        </button-more-options-option>

        <button-more-options-option-with-confirmation
            v-if="allowArchive && lead.can_archive && ! lead.is_archived"
            :confirm-text="_mft('crm:archive.confirm.single')"
            :confirm-button-label="_mft('crm:archive.archive')"
            :abort-button-label="_mft('shared:action.cancel')"
            @confirmed="archiveLead"
            class="w-48"
        >
            {{ _mft('crm:archive.archive') }}
        </button-more-options-option-with-confirmation>

        <button-more-options-option
            v-if="allowDelete && ! lead.has_user"
            style-type="danger"
            @click="deleteDialogOpen = true"
            class="w-48"
        >
            {{ _mft('crm:lead.delete.label') }}
        </button-more-options-option>
    </button-more-options>

    <modal-basic
        :open="deleteDialogOpen"
        @closed="deleteDialogOpen = false"
    >
        <template #header>
            {{ _mft('crm:lead.delete.label') }}
        </template>
        <template #body>
            <div class="space-y-4">
                <p class="text-sm text-gray-700">
                    {{ _mft('crm:lead.delete.cannotBeUndone') }}
                </p>

                <p
                    v-if="deleteContextState === 'loaded' && isContactSynced"
                    class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3"
                >
                    {{ _mft('crm:lead.delete.rememberDeleteSynced') }}
                </p>

                <p
                    v-else-if="deleteContextState === 'error'"
                    class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
                >
                    {{ _mft('error:error.generalTryAgainLater') }}
                </p>

                <div
                    v-else-if="deleteContextState === 'loading'"
                    class="flex items-center gap-2 text-sm text-gray-500"
                >
                    <mf-spinner-small class="w-4 h-4" />
                    <span>{{ _mft('crm:lead.delete.loadingSyncStatus') }}</span>
                </div>

                <div class="flex justify-end space-x-2">
                    <button-secondary @click="deleteDialogOpen = false">
                        {{ _mft('shared:action.cancel') }}
                    </button-secondary>
                    <button-base
                        style-type="danger"
                        :disabled="deleteContextState === 'loading'"
                        @click="deleteLead"
                    >
                        {{ _mft('crm:lead.delete.confirm') }}
                    </button-base>
                </div>
            </div>
        </template>
    </modal-basic>
</template>
